import { useEffect, useLayoutEffect, useRef } from "react";

export type IntersectionObserverOptions = {
  once?: boolean;
  rootMargin?: string;
  onChange: (visible: boolean) => void;
  /**
   * This property is used to enable or disable the IntersectionObserver.
   *
   * @default true
   */
  enabled?: boolean;
};

export function useIntersectionObserver<T extends HTMLElement = HTMLElement>(
  target: T | null,
  { once, rootMargin, onChange, enabled = true }: IntersectionObserverOptions,
) {
  const wasIntersecting = useRef(false);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (wasIntersecting.current === entry.isIntersecting) {
          return;
        }

        wasIntersecting.current = entry.isIntersecting;

        onChangeRef.current(entry.isIntersecting);

        if (once && entry.isIntersecting) {
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [target, once, rootMargin, enabled]);
}
